import { FC, JSX, useContext, useEffect, useMemo, useState } from "react"
import { FlyoutFooter } from "@app/shared/Flyouts";
import { SortDirections, WafflesPaginationProps, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { EnterpriseComplianceDashboardContext } from "../contexts/EcdContext";
import { Link } from "react-router-dom";
import { sharedEcdDrillInColumnConfig, sharedEcdDrillInCustomSort, sharedEcdDrillInPaginationProps, sharedEcdDrillInSlicedTableData } from "./EcdDrillInConfig";
import { SearchBox } from "@app/shared/SearchBox";

export interface EcdExceptionsDrillInTableData {
  [wafflesKeyColumnName]: string;
  appealed: string;
  approved: string;
  denied: string;
  pending: string;
  projectName: JSX.Element;
  submitted: string;
}

const EcdExceptionsFlyout: FC = () => {
  const ecdContext = useContext(EnterpriseComplianceDashboardContext);
  const [data, setData] = useState<EcdExceptionsDrillInTableData[]>([]);
  const [filterString, setFilterString] = useState("");
  const [skip, setSkip] = useState(0);
  const take = 100;

  useEffect(() => {
    ecdContext.getExceptionsDrillInData().then((response) => {
      const newData: EcdExceptionsDrillInTableData[] = response.map((row) => {
        return {
          /* oxlint-disable sort-keys */
          [wafflesKeyColumnName]: row.customProjectId.toString(),
          projectName: <Link key={row.projectName} to={`/projects/${row.customProjectId}/adequacy?viewBy=ALL&all=false&exceptions=true`} target="_blank">{row.projectName}</Link>,
          approved: row.exceptionsApproved.toLocaleString(),
          pending: row.exceptionsPending.toLocaleString(),
          submitted: row.exceptionsSubmitted.toLocaleString(),
          appealed: row.exceptionsAppealed.toLocaleString(),
          denied: row.exceptionsDenied.toLocaleString(),
          /* oxlint-enable sort-keys */
        }
      });
      customSort(newData, "approved", SortDirections.Desc);
    })
  }, []);

  const customSort = (records: EcdExceptionsDrillInTableData[], column: keyof EcdExceptionsDrillInTableData, direction: SortDirections) => {
    const newData = sharedEcdDrillInCustomSort<EcdExceptionsDrillInTableData>(
      data.length > 0 ? data : records,
      column,
      direction,
      "approved",
      SortDirections.Desc
    );

    setData(newData);
    return records;
  }

  const paginationProps: WafflesPaginationProps = useMemo(() => (
    sharedEcdDrillInPaginationProps<EcdExceptionsDrillInTableData>(skip, take, setSkip, data, filterString)
  ), [data, skip, take, filterString]);

  const slicedData = useMemo(() => {
    return sharedEcdDrillInSlicedTableData<EcdExceptionsDrillInTableData>(data, filterString, skip, take);
  }, [data, skip, take, filterString]);

  return (
    <>
      <SearchBox
        fieldsetClass="row mx-0"
        inputLabel="Filter on Project Name"
        onSubmit={(value) => setFilterString(value)}
        testId={`ecd-exceptions-flyout__filter`}
      />
      <Waffles<EcdExceptionsDrillInTableData>
        columns={sharedEcdDrillInColumnConfig}
        customSort={customSort}
        data={slicedData}
        enablePagination={true}
        fullWidth={true}
        loading={data.length === 0 && filterString === ""}
        paginationProps={paginationProps}
        passThroughSorting={true}
        testId="ecd-exceptions-flyout__table"
      />
      <FlyoutFooter
        submitButton={null}
      />
    </>
  );
}

export default EcdExceptionsFlyout;
